import {toast} from 'react-toastify';
import jwt_decode from 'jwt-decode';
import {
  getLocationEpoch,
  isStaleLocationGet,
  pendingLocationRequest
} from 'helpers/services/utils/locationEpoch';
import store from '../../../store';
import axiosAPI from './interseptors';
import {getAppIp, getAppType, getAppVersion} from '../../app.helper';
import {logoutFunction} from '../../functionUtils/logout';
import {getStorageToken} from '../../localStorage/authStorage';
import {DELETE_REFRESH_TOKEN} from '../constants/API_ENDPOINTS';

const isPasswordChanged = (error) =>
  error?.response?.status === 401 &&
  error?.response?.data?.message === 'Password was changed';

/**
 * Request function
 * @param method {'post' | 'get' | 'put' | 'patch' | 'delete'}
 * @param url {string}
 * @param data {object=}
 * @param config {object=}
 * @param config.ignoreLocationEpoch {boolean=} Skip the stale-location drop.
 *   For GETs that do not depend on the active location.
 * @return {import('axios').AxiosResponse<any>}
 */
export const request = async (method, url, data, config) => {
  const epoch = getLocationEpoch();
  // A password-change 401 must still log the user out, even if the
  // location changed while that GET was in flight.
  const dropStaleLocationGet = (error) =>
    !config?.ignoreLocationEpoch &&
    isStaleLocationGet(method, epoch) &&
    !isPasswordChanged(error);

  try {
    const axiosInstance = axiosAPI;

    axiosInstance.defaults.headers.common[
      'X-App-Type'
    ] = `${getAppType()} ${getAppVersion()}`;
    axiosInstance.defaults.headers.common['X-App-Ip'] = `${getAppIp()}`;

    const response = await axiosInstance({
      method,
      url,
      ...data,
      headers: config?.headers,
      responseType: config?.responseType
    });

    // Location changed while this GET was in flight. Do not hand the body
    // back: the caller would store the previous location as the current one.
    if (dropStaleLocationGet()) {
      return pendingLocationRequest();
    }

    return response;
  } catch (error) {
    if (dropStaleLocationGet(error)) {
      return pendingLocationRequest();
    }

    const {response} = error;
    const {
      globalNotificationsReducer: {isNetworkOnline}
    } = store.getState();

    if (error.message === 'Cancel Offline') {
      return undefined;
    }

    if (
      response?.status === 401 &&
      response?.data?.message === 'Password was changed'
    ) {
      toast.error(response.data.message);
      const tokens = getStorageToken();
      await request(
        'delete',
        DELETE_REFRESH_TOKEN(jwt_decode(tokens.token || tokens.refreshToken).id)
      );
      await logoutFunction();
    }

    if (isNetworkOnline) {
      if (!config?.silent) {
        if (response) {
          if (response.data.errors && response.data.errors.length) {
            response.data.errors.forEach(({message}) => toast.error(message));
          }

          if (response.data.message) {
            toast.error(response.data.message);
          }
        } else {
          toast.error(`Error occurred - please try again [${error}]`);
        }
      }

      throw error;
    }

    return false;
  }
};

export const testAutomationService = async (method, url, data, config) => {
  const newConf = config || {};

  return request(method, url, data, {
    ...newConf,
    headers: {
      Authorization:
        'Basic cHJvZGV2QGdldHNhdWNlLmNvbTpFN2pLUURVczdhamRYd2tzMElzOGY0WWpUU0ZqWlF2Sw=='
    }
  });
};
